import { EntityNormalization } from "../../annotator/interfaces/entity";
import { NormalizationType } from "../interfaces/normalization";
import {
  ConversionInput,
  ConversionMeta,
  StepMeta,
} from "../interfaces/conversion";
import {
  CategorizationAnnotation,
  CategorizationWithConfig,
} from "../interfaces/category";
import { getInLanguageOrDefault } from "../../common/utilities/language";
import { CUSTOMER_NORMALIZED_ENTITY_IDS } from "./customerNormalization";

export const TYPES_BASE = "conversion.types";
export const CUSTOMER_BASE = "conversion.customer";

// Types that consume a categorization (number → decimal separator, date → region).
export const CATEGORY_DRIVEN_TYPES = [
  NormalizationType.DATE.toLowerCase(),
  NormalizationType.NUMBER.toLowerCase(),
  NormalizationType.NUMBER_RAVAGO.toLowerCase(),
  NormalizationType.PRICE_RAVAGO.toLowerCase(),
];

// Customer router strategy types (route to the customer normalization API).
export const CUSTOMER_STRATEGY_TYPES = ["csv", "csvedmac"];

const DEFAULT_META = { icon: "bi-arrow-repeat", key: "default" };

// Maps a strategy type to its icon and translation key (under
// detail:conversion.types.*). hasFormat → uses the {{format}} variant.
const TYPE_META: Record<
  string,
  { icon: string; key: string; hasFormat?: boolean }
> = {
  [NormalizationType.DATE.toLowerCase()]: {
    icon: "bi-calendar-event",
    key: "date",
    hasFormat: true,
  },
  [NormalizationType.WEEK.toLowerCase()]: {
    icon: "bi-calendar-week",
    key: "week",
    hasFormat: true,
  },
  [NormalizationType.NUMBER.toLowerCase()]: { icon: "bi-123", key: "number" },
  [NormalizationType.NUMBER_RAVAGO.toLowerCase()]: {
    icon: "bi-123",
    key: "number",
  },
  [NormalizationType.PRICE_RAVAGO.toLowerCase()]: {
    icon: "bi-currency-euro",
    key: "price",
  },
  iban: { icon: "bi-bank", key: "iban" },
  countrytoisocode: { icon: "bi-globe", key: "country" },
  usastatetocode: { icon: "bi-geo-alt", key: "usaState" },
  coaidravago: { icon: "bi-upc-scan", key: "accountId" },
  dictionarystringmapping: { icon: "bi-table", key: "mapping" },
  texttransformation: { icon: "bi-type", key: "text" },
};

export const getStrategyType = (normalization: EntityNormalization): string =>
  (normalization.normalizationStrategy?.["type"] ?? "")
    .toString()
    .toLowerCase();

// A method is "customer" when it isn't a standard normalizer (those all start
// with "normalize"), and isn't a manual edit or error marker.
export const isCustomerMethod = (method?: string | null): boolean => {
  if (!method) {
    return false;
  }
  const lower = method.toLowerCase();
  return (
    lower !== "manual" && lower !== "iserror" && !lower.startsWith("normalize")
  );
};

export const getConversionMeta = (
  normalization: EntityNormalization,
): ConversionMeta => {
  const meta = TYPE_META[getStrategyType(normalization)] ?? DEFAULT_META;
  return {
    ...meta,
    base: TYPES_BASE,
    format: normalization.normalizationStrategy?.["format"],
    description: normalization.description,
  };
};

// Customer via router strategy (Edmac), org-triggered set, or runtime method.
export const resolveIsCustomer = (
  normalizations: Array<EntityNormalization>,
  entityTypeId: string | undefined,
  hasCustomerMethod: boolean,
): boolean => {
  const hasCustomerStrategy = normalizations.some((normalization) =>
    CUSTOMER_STRATEGY_TYPES.includes(getStrategyType(normalization)),
  );
  const isOrgTriggered =
    !!entityTypeId && CUSTOMER_NORMALIZED_ENTITY_IDS.includes(entityTypeId);
  return hasCustomerStrategy || isOrgTriggered || hasCustomerMethod;
};

// AI steps (router strategies excluded) plus a generic customer step.
export const buildStepMetas = (
  sortedNormalizations: Array<EntityNormalization>,
  isCustomer: boolean,
): Array<StepMeta> => {
  const stepMetas: Array<StepMeta> = sortedNormalizations
    .filter(
      (normalization) =>
        !CUSTOMER_STRATEGY_TYPES.includes(getStrategyType(normalization)),
    )
    .map((normalization) => ({
      meta: getConversionMeta(normalization),
      categoryDriven: CATEGORY_DRIVEN_TYPES.includes(
        getStrategyType(normalization),
      ),
    }));

  if (isCustomer) {
    stepMetas.push({
      meta: { icon: "bi-building", key: "generic", base: CUSTOMER_BASE },
      categoryDriven: false,
    });
  }

  return stepMetas;
};

export const usesCategoryContext = (
  normalizations: Array<EntityNormalization>,
  isCustomer: boolean,
): boolean =>
  isCustomer ||
  normalizations.some((normalization) =>
    CATEGORY_DRIVEN_TYPES.includes(getStrategyType(normalization)),
  );

// Linked categorizations → config-input tags, deduped by id. Unfilled inputs
// are kept (with notSetLabel) since a missing dependency is itself meaningful.
export const buildConfigInputs = (
  linkedCategorizations: Array<CategorizationWithConfig>,
  categorizationAnnotations: Array<CategorizationAnnotation>,
  language: string,
  notSetLabel: string,
): Array<ConversionInput> => {
  const inputs: Array<ConversionInput> = [];
  const seenCategorizationIds = new Set<string>();

  for (const linked of linkedCategorizations) {
    const categorizationId = linked.categorization.id;
    if (seenCategorizationIds.has(categorizationId)) {
      continue;
    }
    seenCategorizationIds.add(categorizationId);

    const annotation = categorizationAnnotations.find(
      (ca) => ca.categorizationId === categorizationId,
    );
    const value = annotation?.visibleValue ?? annotation?.value;
    inputs.push({
      kind: "config",
      label: getInLanguageOrDefault(linked.categorization.label, language),
      value: value || notSetLabel,
      isEmpty: !value,
    });
  }

  return inputs;
};
